import { useConversations } from "@coaching-culture/api-client";
import {
  Button,
  DatePill,
  EmptyState,
  Panel,
  Table,
  UserFlag,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { fromUnixTime } from "date-fns";
import { differenceInDays } from "date-fns/esm";
import { FaCommentDots, FaEye } from "react-icons/fa";
import { useLocalStorage } from "react-use";
import { useTheme } from "styled-components";
import { WidgetPanelHeader } from "./WidgetPanelHeader";

export const RecentConversationsWidget = () => {
  const { data: convos } = useConversations();
  const [includeIncomplete, setIncludeIncomplete] = useLocalStorage(
    "convo-widget-incomp",
    false,
  );
  const user = useUser();
  const theme = useTheme();

  const mappedConvos = (convos ?? []).flatMap((x) =>
    x.occurences
      .map((o) => ({
        ...x,
        ...o,
        cancelledOn: o.cancelledOn ?? x.cancelledOn,
      }))
      .filter(
        (x) => fromUnixTime(x.scheduledDate + x.duration * 60) < new Date(),
      )
      .filter((x) => {
        const d = Math.abs(
          differenceInDays(fromUnixTime(x.scheduledDate), new Date()),
        );
        return d < 6 * 30;
      })
      .filter((x) => includeIncomplete || x.completedOn != null)
      .filter((x) => x.cancelledOn == null),
  );

  const notMe = (convo: (typeof mappedConvos)[number]) =>
    convo.directReport.id === user[0].id ? convo.manager : convo.directReport;

  return (
    <Panel width={"100%"}>
      <WidgetPanelHeader title="Recent conversations">
        <Toggle
          leftLabel="Completed"
          rightLabel="All"
          value={includeIncomplete}
          onChange={setIncludeIncomplete}
        />
      </WidgetPanelHeader>

      <ScrollableContainer maxHeight={310}>
        <Table>
          <thead
            style={{
              top: 0,
              background: theme.colors.surface,
              position: "sticky",
              marginBottom: "20px",
              zIndex: 3,
            }}
          >
            <tr>
              <th>With whom</th>
              <th>Scheduled on</th>
              <th>Completed on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {mappedConvos.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState compact icon={FaCommentDots}>
                    No conversations in the last six months.
                  </EmptyState>
                </td>
              </tr>
            ) : null}
            {mappedConvos
              .sort((a, b) => b.scheduledDate - a.scheduledDate)
              .map((x) => (
                <tr>
                  <td>
                    <UserFlag user={notMe(x)} />
                  </td>
                  <td>
                    <DatePill value={x.scheduledDate} />
                  </td>
                  <td>
                    <DatePill
                      value={x.completedOn}
                      color={x.completedOn == null ? undefined : "positive"}
                    />
                  </td>
                  <td>
                    <Button
                      icon={FaEye}
                      to={`/solutions/performance/conversations/${x.id}/${x.index}/play`}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </ScrollableContainer>
    </Panel>
  );
};
